import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { 
  Users, 
  BarChart3, 
  Settings, 
  Plus,
  TrendingUp,
  Heart,
  AlertTriangle,
  Calendar,
  Mail,
  Clock,
  ArrowRight,
  Sparkles,
  Shield,
  MessageCircle,
  CheckCircle,
  Building2
} from 'lucide-react'

import { useAuth } from '../../hooks/useAuth'
import { Navigation } from '../../components/Navigation'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { db } from '../../lib/supabase'

export const Dashboard: React.FC = () => {
  const { t } = useTranslation()
  const { user, appUser, refreshUser } = useAuth()
  const [currentTime, setCurrentTime] = useState(new Date())
  const [userSetupNeeded, setUserSetupNeeded] = useState(false)
  const [companySetupNeeded, setCompanySetupNeeded] = useState(false)
  const [loading, setLoading] = useState(false)
  const [employeeCount, setEmployeeCount] = useState(0)

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000)
    return () => clearInterval(timer)
  }, [])

  useEffect(() => {
    // Check if we need to create user record
    if (user && !appUser) {
      setUserSetupNeeded(true)
    } else if (appUser && !appUser.company_id) {
      setCompanySetupNeeded(true)
    } else if (appUser?.company_id) {
      loadEmployeeCount(appUser.company_id)
    }
  }, [user, appUser])

  const loadEmployeeCount = async (companyId: string) => {
    try {
      const { data, error } = await db.employees.getByCompany(companyId)
      if (!error && data) {
        setEmployeeCount(data.length)
      }
    } catch (error) {
      console.error('Error loading employee count:', error)
    }
  }

  const handleUserSetup = async () => {
    if (!user) return

    setLoading(true)
    try {
      console.log('🔧 Setting up user record...')
      
      // First, try to fetch the user record in case it exists but wasn't loaded due to RLS
      const { data: existingUser, error: fetchError } = await db.users.findById(user.id)
      
      if (existingUser) {
        console.log('✅ User record already exists, refreshing auth state...')
        if (refreshUser) {
          await refreshUser()
        }
        setUserSetupNeeded(false)
        return
      }

      // If no existing user found, try to create one
      const { data, error } = await db.users.create({
        id: user.id,
        email: user.email || '',
        role: 'HR', // Default to HR for now
        company_id: null, // Will be set when company is created
        status: 'active',
      })

      if (error) {
        console.error('❌ User setup error:', error)
        
        // Handle duplicate key error specifically
        if (error.code === '23505') {
          console.log('🔄 User record already exists (duplicate key), refreshing data...')
          if (refreshUser) {
            await refreshUser()
          }
          setUserSetupNeeded(false)
        } else {
          // Handle other types of errors
          console.error('❌ Unexpected error during user setup:', error.message)
        }
      } else {
        console.log('✅ User setup successful')
        if (refreshUser) {
          await refreshUser()
        }
        setUserSetupNeeded(false)
        setCompanySetupNeeded(true)
      }
    } catch (error) {
      console.error('❌ User setup exception:', error)
      
      // If it's a network error or other exception, try refreshing user data
      if (refreshUser) {
        try {
          await refreshUser()
          setUserSetupNeeded(false)
        } catch (refreshError) {
          console.error('❌ Failed to refresh user data:', refreshError)
        }
      }
    } finally {
      setLoading(false)
    }
  }

  const handleCompanySetup = async () => {
    if (!user || !appUser) return

    setLoading(true)
    try {
      console.log('🏢 Setting up company...')
      
      // Create a default company
      const companyName = appUser.email.split('@')[1]?.split('.')[0] || 'My Company'
      
      const { data: company, error: companyError } = await db.companies.create({
        name: companyName.charAt(0).toUpperCase() + companyName.slice(1),
        industry: 'Technology',
      })

      if (companyError) {
        console.error('❌ Company setup error:', companyError)
        return
      }

      if (!company) {
        console.error('❌ Company creation failed: No data returned')
        return
      }

      // Update user with company ID
      const { error: updateError } = await db.users.update(appUser.id, {
        company_id: company.id
      })

      if (updateError) {
        console.error('❌ User update error:', updateError)
        return
      }

      console.log('✅ Company setup successful')
      if (refreshUser) {
        await refreshUser()
      }
      setCompanySetupNeeded(false)
    } catch (error) {
      console.error('❌ Company setup exception:', error)
    } finally {
      setLoading(false)
    }
  }

  const quickActions = [
    {
      title: 'Add Team Members',
      description: 'Invite employees to join your wellness program',
      icon: Plus,
      action: () => window.location.href = '/employees',
      color: 'bg-sage-500',
      gradient: 'from-sage-500 to-emerald-600'
    },
    {
      title: 'View Wellness Reports',
      description: 'Analyze team mental health insights',
      icon: BarChart3,
      action: () => window.location.href = '/reports',
      color: 'bg-blue-500',
      gradient: 'from-blue-500 to-blue-600'
    },
    {
      title: 'Configure Settings',
      description: 'Customize your wellness program',
      icon: Settings,
      action: () => window.location.href = '/settings',
      color: 'bg-slate-600',
      gradient: 'from-slate-600 to-slate-700'
    }
  ]

  const stats = [
    {
      title: 'Team Members',
      value: employeeCount.toString(),
      change: employeeCount > 0 ? `${employeeCount} active` : 'Getting started',
      icon: Users,
      color: 'text-sage-600',
      bgColor: 'bg-sage-100',
      trend: 'neutral'
    },
    {
      title: 'Wellness Score',
      value: '--',
      change: 'No data yet',
      icon: Heart,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
      trend: 'neutral'
    },
    {
      title: 'Engagement Rate',
      value: '--',
      change: 'No data yet',
      icon: TrendingUp,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-100',
      trend: 'neutral'
    },
    {
      title: 'Care Alerts',
      value: '0',
      change: 'All good',
      icon: AlertTriangle,
      color: 'text-amber-600',
      bgColor: 'bg-amber-100',
      trend: 'neutral'
    }
  ]

  return (
    <div className="min-h-screen bg-calming">
      <Navigation variant="dashboard" />
      
      {/* Header */}
      <div className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col lg:flex-row lg:items-center lg:justify-between"
          >
            <div className="flex-1">
              <div className="flex items-center mb-2">
                <div className="w-12 h-12 bg-gradient-to-r from-sage-500 to-emerald-600 rounded-2xl flex items-center justify-center mr-4">
                  <Heart className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-slate-900">
                    Welcome to Mentra! 👋
                  </h1>
                  <p className="text-slate-600">
                    {user?.email} • {currentTime.toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </p>
                </div>
              </div>
              <p className="text-slate-600 max-w-2xl">
                Your team's wellness sanctuary is ready. Let's start building a healthier, happier workplace together.
              </p>
            </div>
            <div className="mt-4 lg:mt-0 flex gap-3">
              <Link to="/employees">
                <Button variant="outline" className="flex items-center border-slate-300 text-slate-700 hover:bg-slate-50">
                  <Users className="w-4 h-4 mr-2" />
                  Manage Team
                </Button>
              </Link>
              <Link to="/employees">
                <Button className="bg-sage-600 hover:bg-sage-700 text-white">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Members
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* User Setup Notice */}
        {userSetupNeeded && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <Card className="p-6 bg-blue-50 border-blue-200">
              <div className="flex items-start">
                <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                  <Sparkles className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-blue-900 mb-2">
                    Setting up your account...
                  </h3>
                  <p className="text-blue-700 mb-4">
                    We need to create your user profile to get started. This only takes a moment.
                  </p>
                  <Button 
                    onClick={handleUserSetup}
                    loading={loading}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    Complete Setup
                  </Button>
                </div>
              </div>
            </Card>
          </motion.div>
        )}

        {/* Company Setup Notice */}
        {!userSetupNeeded && companySetupNeeded && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <Card className="p-6 bg-emerald-50 border-emerald-200">
              <div className="flex items-start">
                <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                  <Building2 className="w-5 h-5 text-emerald-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-emerald-900 mb-2">
                    Create Your Company
                  </h3>
                  <p className="text-emerald-700 mb-4">
                    We need to set up your company profile to continue. This will allow you to manage your team.
                  </p>
                  <Button 
                    onClick={handleCompanySetup}
                    loading={loading}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    Create Company
                  </Button>
                </div>
              </div>
            </Card>
          </motion.div>
        )}

        {/* Stats Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
        >
          {stats.map((stat, index) => (
            <motion.div
              key={stat.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + index * 0.1 }}
              whileHover={{ y: -2 }}
            >
              <Card className="p-6 border-2 border-transparent hover:border-slate-200 transition-all duration-300">
                <div className="flex items-center justify-between mb-4">
                  <div className={`p-3 rounded-xl ${stat.bgColor}`}>
                    <stat.icon className={`w-6 h-6 ${stat.color}`} />
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-600 mb-1">
                    {stat.title}
                  </p>
                  <p className="text-3xl font-bold text-slate-900 mb-1">
                    {stat.value}
                  </p>
                  <p className="text-xs text-slate-500">
                    {stat.change}
                  </p>
                </div>
              </Card>
            </motion.div>
          ))}
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
          {/* Quick Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="lg:col-span-2"
          >
            <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center">
              <Sparkles className="w-5 h-5 mr-2 text-sage-600" />
              Quick Actions
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {quickActions.map((action, index) => (
                <motion.div
                  key={action.title}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.3 + index * 0.1 }}
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Card 
                    className="p-6 cursor-pointer border-2 border-transparent hover:border-slate-200 transition-all duration-300 h-full"
                    onClick={action.action}
                  >
                    <div className={`w-12 h-12 bg-gradient-to-r ${action.gradient} rounded-xl flex items-center justify-center mb-4`}>
                      <action.icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="font-semibold text-slate-900 mb-2">
                      {action.title}
                    </h3>
                    <p className="text-sm text-slate-600 mb-4">
                      {action.description}
                    </p>
                    <div className="flex items-center text-sage-600 text-sm font-medium">
                      Get started
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Getting Started */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center">
              <CheckCircle className="w-5 h-5 mr-2 text-emerald-600" />
              Getting Started
            </h2>
            <Card className="p-6">
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle className="w-4 h-4 text-emerald-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-slate-900 text-sm">
                      Account Created
                    </h4>
                    <p className="text-xs text-slate-600 mt-1">
                      Your Mentra account is ready to go
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className={`w-6 h-6 ${appUser?.company_id ? 'bg-emerald-100' : 'bg-slate-200'} rounded-full flex items-center justify-center flex-shrink-0 mt-0.5`}>
                    {appUser?.company_id ? (
                      <CheckCircle className="w-4 h-4 text-emerald-600" />
                    ) : (
                      <span className="text-xs font-medium text-slate-600">2</span>
                    )}
                  </div>
                  <div>
                    <h4 className="font-medium text-slate-900 text-sm">
                      Company Setup
                    </h4>
                    <p className="text-xs text-slate-600 mt-1">
                      {appUser?.company_id 
                        ? 'Your company is set up and ready'
                        : 'Create your company profile'
                      }
                    </p>
                    {!appUser?.company_id && (
                      <Button 
                        size="sm" 
                        onClick={handleCompanySetup}
                        loading={loading}
                        className="mt-2 bg-sage-600 hover:bg-sage-700 text-white text-xs"
                      >
                        Create Company
                      </Button>
                    )}
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className={`w-6 h-6 ${employeeCount > 0 ? 'bg-emerald-100' : 'bg-slate-200'} rounded-full flex items-center justify-center flex-shrink-0 mt-0.5`}>
                    {employeeCount > 0 ? (
                      <CheckCircle className="w-4 h-4 text-emerald-600" />
                    ) : (
                      <span className="text-xs font-medium text-slate-600">3</span>
                    )}
                  </div>
                  <div>
                    <h4 className="font-medium text-slate-900 text-sm">
                      Add Team Members
                    </h4>
                    <p className="text-xs text-slate-600 mt-1">
                      Invite your team to start their wellness journey
                    </p>
                    {appUser?.company_id && employeeCount === 0 && (
                      <Link to="/employees">
                        <Button size="sm" className="mt-2 bg-sage-600 hover:bg-sage-700 text-white text-xs">
                          Add Members
                        </Button>
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>

        {/* Welcome Message */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Card className="p-8 bg-gradient-to-r from-sage-50 to-emerald-50 border-sage-200">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-sage-500 to-emerald-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Heart className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2">
                Welcome to Your Wellness Journey
              </h3>
              <p className="text-slate-600 mb-6 max-w-2xl mx-auto">
                Mentra helps you create a supportive workplace where mental health thrives. 
                Start by adding your team members and exploring the wellness insights.
              </p>
              <div className="flex flex-wrap justify-center gap-4 text-sm text-slate-600 mb-6">
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-sage-500 rounded-full mr-2"></div>
                  HIPAA Compliant
                </div>
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full mr-2"></div>
                  AI-Powered Insights
                </div>
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                  Privacy First
                </div>
              </div>
              <Link to="/employees">
                <Button className="bg-sage-600 hover:bg-sage-700 text-white">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Your First Team Member
                </Button>
              </Link>
            </div>
          </Card>
        </motion.div>
      </div>
    </div>
  )
}